import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';

const CARGO_LABELS = {
  furniture: 'أثاث', building_materials: 'مواد بناء', commercial_goods: 'بضائع تجارية',
  parcels: 'طرود', equipment: 'معدات', food_items: 'مواد غذائية', vehicle: 'سيارة', other: 'أخرى',
};

// Display-only label set (a request's vehiclePreference could in theory
// carry any value ever offered).
const VEHICLE_LABELS = {
  rickshaw: 'ركشة', saloon: 'صالون', fourwheel: 'فوريل', box: 'بوكس', bus: 'حافلة',
  pickup: 'بيكب', light_truck: 'دفار', truck_5ton: 'شاحنة 5 طن', truck_10ton: 'شاحنة 10 طن',
  heavy_truck: 'شاحنة ثقيلة', trailer: 'تريلا', container_carrier: 'ناقلة اليات',
  motorcycle: 'دراجة نارية', taxi: 'تاكسي', van: 'فان',
};

const inputCls = 'w-full border-2 border-gray-200 hover:border-blue-400/40 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';

// ── Quote submission modal ──────────────────────────────────────────────────
// Vehicle is selected from the provider's own registered fleet (see
// TransportProviderVehiclesPage) — vehicleCategory is derived from it
// server-side, no free text/static picklist here anymore. Only vehicles
// matching the request's own vehiclePreference (or any, if unset) and with
// driver info on file are offered.
const QuoteModal = ({ request, vehicles, onClose, onDone }) => {
  const existing = request.myQuote;
  const usableVehicles = vehicles.filter((v) => v.isActive && v.driverName && v.driverPhone
    && (!request.vehiclePreference || v.category === request.vehiclePreference));
  const [vehicleId, setVehicleId] = useState(existing?.vehicleId || usableVehicles[0]?.id || '');
  const [price, setPrice] = useState(existing?.price || '');
  const [availableAt, setAvailableAt] = useState('');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [loadingIncluded, setLoadingIncluded] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!price || parseFloat(price) <= 0) { toast.error('يرجى إدخال سعر صحيح'); return; }
    if (!vehicleId) { toast.error('يرجى اختيار مركبة'); return; }
    setSaving(true);
    try {
      await transportProviderAPI.submitQuote(request.id, {
        vehicleId, price, availableAt: availableAt || undefined,
        estimatedDelivery: estimatedDelivery || undefined, loadingIncluded, notes: notes || undefined,
      });
      toast.success('تم إرسال العرض');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر إرسال العرض');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-bold text-gray-800">{existing ? 'تعديل العرض' : 'تقديم عرض'} — {request.requestNumber}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        {usableVehicles.length === 0 ? (
          <div className="p-5 text-sm text-amber-700 bg-amber-50">
            لا توجد مركبة نشطة مطابقة لنوع هذا الطلب ببيانات سائق مكتملة. أضف مركبة مناسبة أو أكمل بيانات السائق من صفحة «أسطولي».
          </div>
        ) : (
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className={labelCls}>المركبة والسائق *</label>
            <select className={inputCls} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              {usableVehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {VEHICLE_LABELS[v.category] || v.category} — {v.driverName} — {v.plateNumber || 'بلا لوحة'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>السعر (ج.س) *</label>
            <input type="number" step="0.01" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} required />
          </div>
          <div>
            <label className={labelCls}>وقت التوفر</label>
            <input type="datetime-local" className={inputCls} value={availableAt} onChange={(e) => setAvailableAt(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>مدة التسليم المتوقعة</label>
            <input className={inputCls} placeholder="مثال: نفس اليوم" value={estimatedDelivery} onChange={(e) => setEstimatedDelivery(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <input type="checkbox" checked={loadingIncluded} onChange={(e) => setLoadingIncluded(e.target.checked)} /> التحميل مشمول بالسعر
          </label>
          <div>
            <label className={labelCls}>ملاحظات</label>
            <textarea rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2.5 rounded-xl font-semibold transition">
              {saving ? 'جارٍ الإرسال...' : 'إرسال العرض'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

const TransportProviderJobBoardPage = () => {
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [quoteFor, setQuoteFor] = useState(null);

  const load = () => {
    setLoading(true);
    transportProviderAPI.getOpenRequests()
      .then((res) => setRequests(res.data.data || []))
      .catch(() => toast.error('فشل في تحميل الطلبات'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    transportProviderAPI.getVehicles().then((res) => setVehicles(res.data.data || [])).catch(() => {});
  }, []);

  // Best-effort location so the backend can rank the board nearest-first —
  // silently does nothing if the browser/user denies permission, in which
  // case the list just stays in its previous newest-first order.
  useEffect(() => {
    const reportLocation = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => { transportProviderAPI.updateMyLocation(pos.coords.latitude, pos.coords.longitude).catch(() => {}); },
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
      );
    };
    reportLocation();
    const locInterval = setInterval(reportLocation, 5 * 60 * 1000);
    return () => clearInterval(locInterval);
  }, []);

  useEffect(() => {
    load();
    let interval = setInterval(load, 15000); // cargo — less time-sensitive than rides' 10s poll
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load();
        interval = setInterval(load, 15000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <div dir="rtl" className="p-4 sm:p-6">
      <h1 className="text-xl font-black text-gray-800 mb-1">📋 طلبات النقل المتاحة</h1>
      <p className="text-sm text-gray-500 mb-6">قدّم عرضاً على أي طلب يناسب أسطولك — مرتبة حسب الأقرب إليك عند توفر موقعك، ومطابقة لأنواع مركباتك المسجّلة</p>

      {loading ? (
        <div className="text-center text-gray-400 py-16">جاري التحميل...</div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">
          <div className="text-3xl mb-2">📭</div>
          <p>لا توجد طلبات نقل مطابقة الآن</p>
          <p className="text-xs mt-1">تأكد من تفعيل الموقع في متصفحك ومن تسجيل مركبة مطابقة لنوع الطلب في «أسطولي»</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono text-gray-400">{r.requestNumber}</span>
                <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">
                  {CARGO_LABELS[r.cargoCategory] || r.cargoCategory}
                </span>
              </div>
              <p className="text-sm font-bold text-gray-800 mb-1">{r.fromLabel} ← {r.toLabel}</p>
              {r.tripDistanceKm != null && <p className="text-xs font-semibold text-gray-600 mb-2">🛣️ مسافة الرحلة: {r.tripDistanceKm} كم</p>}
              {r.cargoDescription && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{r.cargoDescription}</p>}
              <div className="flex gap-3 text-xs text-gray-600 mb-3 flex-wrap">
                {r.weightKg && <span>⚖️ {r.weightKg} كجم</span>}
                {r.quantity && <span>📦 {r.quantity} قطعة</span>}
                {r.loadingDate && <span>📅 {new Date(r.loadingDate).toLocaleDateString('ar-SA')}</span>}
                {r.distanceKm != null && <span className="font-semibold text-blue-600">📍 يبعد عنك {r.distanceKm} كم</span>}
              </div>
              {r.vehiclePreference && (
                <p className="text-xs text-gray-500 mb-3">المركبة المفضلة: <span className="font-semibold text-gray-700">{VEHICLE_LABELS[r.vehiclePreference] || r.vehiclePreference}</span></p>
              )}
              {r.myQuote ? (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-700">عرضك: {r.myQuote.price} ج.س</span>
                  <button onClick={() => setQuoteFor(r)} className="text-xs font-semibold text-blue-700 hover:underline">تعديل</button>
                </div>
              ) : (
                <button
                  onClick={() => setQuoteFor(r)}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold py-2 rounded-xl transition"
                >
                  تقديم عرض
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {quoteFor && (
        <QuoteModal request={quoteFor} vehicles={vehicles} onClose={() => setQuoteFor(null)} onDone={() => { setQuoteFor(null); load(); }} />
      )}
    </div>
  );
};

export default TransportProviderJobBoardPage;
